'use client'
import { consoleLoginUrl } from '../lib/urls'
import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import Icon from './Icon'
import type { IconName } from './Icon'
import { useLang } from '../context/LangContext'
import { t, nav, productsDropdown } from '../lib/translations'
import type { Locale } from '../context/LangContext'
import LogoLink from './LogoLink'

const productKeys = ['vps', 'gpu', 'paas', 'database', 'storage', 'backup', 'security', 'devops', 'org', 'monitoring', 'streaming', 'sentry', 'mcp', 'consulting', 'quickapps'] as const

const productIcons: Record<typeof productKeys[number], IconName> = {
  vps: 'server-stack',
  gpu: 'gpu',
  paas: 'container',
  database: 'database',
  storage: 's3',
  backup: 'refresh',
  security: 'shield-check',
  devops: 'code',
  org: 'users',
  monitoring: 'chart-bar',
  streaming: 'video-camera',
  sentry: 'sentry',
  mcp: 'lightning',
  consulting: 'support',
  quickapps: 'lightning',
}

const navLinkKeys: { key: keyof typeof nav; href: string }[] = [
  { key: 'pricing', href: 'pricing' },
  { key: 'calculator', href: 'pricing/calculator' },
  { key: 'mcp', href: 'mcp' },
  { key: 'docs', href: 'docs' },
  { key: 'devops', href: 'devops' },
  { key: 'organizations', href: 'organizations' },
  { key: 'status', href: 'status' },
]

const localeLabels: Record<Locale, string> = { en: 'EN', fa: 'FA', ar: 'AR' }
const localeFlags: Record<Locale, string> = { en: '🇬🇧', fa: '🇮🇷', ar: '🇸🇦' }

export default function Nav() {
  const lang = useLang()
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const [desktopProductsOpen, setDesktopProductsOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const desktopLangRef = useRef<HTMLDivElement>(null)
  const mobileLangRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let ticking = false
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 40)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const inDesktop = desktopLangRef.current?.contains(e.target as Node)
      const inMobile = mobileLangRef.current?.contains(e.target as Node)
      if (!inDesktop && !inMobile) {
        setLangOpen(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const switchLang = (code: Locale) => {
    const segments = pathname.split('/')
    segments[1] = code
    window.location.href = segments.join('/')
  }

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#07070d]/80 backdrop-blur-xl border-b border-white/[0.04]'
          : 'bg-[#07070d]/0 backdrop-blur-xl border-b border-white/[0]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <div className="flex items-center gap-8 xl:gap-12">
            <LogoLink className="flex items-center group shrink-0" />

            <nav className="hidden lg:flex items-center gap-6">
              <div
                className="relative"
                onMouseEnter={() => setDesktopProductsOpen(true)}
                onMouseLeave={() => setDesktopProductsOpen(false)}
              >
                <button className="flex items-center gap-1.5 text-sm text-[#cbd5e1] hover:text-white transition-colors duration-200 cursor-default">
                  {t(lang, nav.products)}
                  <Icon name="chevron-down" className={`w-3 h-3 transition-transform duration-200 ${desktopProductsOpen ? 'rotate-180' : ''}`} />
                </button>
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200 ${
                    desktopProductsOpen ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none'
                  }`}
                >
                  <div className="bg-[#0c0c1a]/95 backdrop-blur-xl rounded-xl p-3 w-[550px] grid grid-cols-2 gap-2 shadow-2xl border border-white/[0.08]">
                    {productKeys.map((key) => {
                      const item = productsDropdown[key]
                      return (
                        <Link
                          key={key}
                          href={`/${lang}/${key === 'org' ? 'organizations' : key === 'quickapps' ? 'quickapps' : key === 'vps' ? 'vps-server' : key}`}
                          onClick={() => setDesktopProductsOpen(false)}
                          className="block h-full px-4 py-3 rounded-xl hover:bg-white/[0.04] transition-all duration-300 group/item"
                        >
                          <div className="flex items-center gap-3.5 h-full">
                            <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center group-hover/item:scale-110 group-hover/item:bg-[rgb(var(--brand-rgb)/0.2)] group-hover/item:shadow-[0_0_15px_rgb(var(--brand-rgb)/0.3)] group-hover/item:border-[rgb(var(--brand-rgb)/0.3)] transition-all duration-300 shrink-0 border border-white/[0.02]">
                              <Icon name={productIcons[key]} className="w-5 h-5 text-[var(--brand-400)] group-hover/item:text-white transition-colors duration-300" />
                            </div>
                            <div>
                              <div className="text-sm text-white font-medium">{t(lang, item.label)}</div>
                              <div className="text-xs text-[#94a3b8] mt-0.5">{t(lang, item.desc)}</div>
                            </div>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </div>
              {navLinkKeys.map((link) => (
                <Link
                  key={link.href}
                  href={`/${lang}/${link.href}`}
                  className="text-sm text-[#cbd5e1] hover:text-white transition-colors duration-200"
                >
                  {t(lang, nav[link.key])}
                </Link>
              ))}
            </nav>
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <div ref={desktopLangRef} className="relative" dir="ltr">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-2.5 h-[42px] rounded-lg border border-white/[0.2] bg-white/[0.03] text-xs font-medium text-white hover:border-white/[0.3] hover:bg-white/[0.1] transition-all duration-200"
              >
                <div className="flex items-center gap-1.5 translate-y-[1.5px]">
                  <span className="text-sm leading-none">{localeFlags[lang]}</span>
                  <span>{localeLabels[lang]}</span>
                </div>
                <Icon name="chevron-down" className={`w-3 h-3 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                className={`absolute top-full right-0 mt-2 bg-[#0c0c1a]/95 backdrop-blur-xl rounded-lg p-1 min-w-[100px] shadow-2xl border border-white/[0.08] transition-all duration-200 origin-top-right ${
                  langOpen ? 'opacity-100 visible scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 invisible scale-95 -translate-y-2 pointer-events-none'
                }`}
              >
                {(Object.keys(localeLabels) as Locale[]).map((code) => (
                  <button
                    key={code}
                    onClick={() => { switchLang(code); setLangOpen(false) }}
                    className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-xs transition-colors ${
                      code === lang ? 'text-white bg-white/[0.06]' : 'text-[#cbd5e1] hover:text-white hover:bg-white/[0.08]'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 translate-y-[1.5px]">
                      <span className="text-sm leading-none">{localeFlags[code]}</span>
                      <span>{localeLabels[code]}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <Link
              href={`/${lang}/contact`}
              className="text-sm font-medium px-4 py-2.5 rounded-lg border border-white/[0.3] bg-white/[0.08] text-white hover:bg-white/[0.15] hover:border-white/[0.4] transition-all duration-200"
            >
              {t(lang, nav.contactUs)}
            </Link>

            <a
              href={consoleLoginUrl}
              className="text-sm font-medium px-5 py-2.5 rounded-lg bg-gradient-to-r from-[var(--brand-600)] to-[var(--brand-500)] hover:from-[var(--brand-500)] hover:to-[var(--brand-300)] transition-all duration-200 shadow-lg shadow-[rgb(var(--brand-rgb)/0.2)] btn-lightning shrink-0"
            >
              {t(lang, nav.loginRegister)}
            </a>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <div ref={mobileLangRef} className="relative" dir="ltr">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/[0.2] bg-white/[0.03] text-xs font-medium text-white hover:bg-white/[0.1] hover:border-white/[0.3] transition-all duration-200"
              >
                <div className="flex items-center gap-1.5 translate-y-[1.5px]">
                  <span className="text-sm leading-none">{localeFlags[lang]}</span>
                  <span>{localeLabels[lang]}</span>
                </div>
              </button>
              <div
                className={`absolute top-full right-0 mt-2 bg-[#0c0c1a]/95 backdrop-blur-xl rounded-lg p-1 min-w-[100px] shadow-2xl border border-white/[0.08] transition-all duration-200 origin-top-right ${
                  langOpen ? 'opacity-100 visible scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 invisible scale-95 -translate-y-2 pointer-events-none'
                }`}
              >
                {(Object.keys(localeLabels) as Locale[]).map((code) => (
                  <button
                    key={code}
                    onClick={() => { switchLang(code); setLangOpen(false) }}
                    className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-xs transition-colors ${
                      code === lang ? 'text-white bg-white/[0.06]' : 'text-[#cbd5e1] hover:text-white hover:bg-white/[0.08]'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 translate-y-[1.5px]">
                      <span className="text-sm leading-none">{localeFlags[code]}</span>
                      <span>{localeLabels[code]}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="relative w-8 h-8 flex items-center justify-center"
              aria-label="Menu"
            >
              <div className="w-5 flex flex-col gap-1.5">
                <span className={`block h-px bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
                <span className={`block h-px bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`block h-px bg-white transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      <div
        className={`md:hidden border-t border-white/[0.04] bg-[#07070d]/95 backdrop-blur-xl overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-6 flex flex-col gap-3">
          <div>
            <button
              onClick={() => setProductsOpen(!productsOpen)}
              className="flex items-center justify-between w-full text-sm text-[#cbd5e1] hover:text-white py-2 transition-colors"
            >
              {t(lang, nav.products)}
              <Icon name="chevron-down" className={`w-3 h-3 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} />
            </button>
            <div
              className={`pl-4 flex flex-col gap-1 mt-1 overflow-hidden transition-all duration-200 ${
                productsOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {productKeys.map((key) => (
                <Link
                  key={key}
                  href={`/${lang}/${key === 'org' ? 'organizations' : key === 'quickapps' ? 'quickapps' : key === 'vps' ? 'vps-server' : key}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2.5 py-1.5 text-sm text-[#94a3b8] hover:text-white transition-colors"
                >
                  <Icon name={productIcons[key]} className="w-4 h-4 shrink-0 text-[var(--brand-400)]" />
                  {t(lang, productsDropdown[key].label)}
                </Link>
              ))}
            </div>
          </div>
          {navLinkKeys.map((link) => (
            <Link
              key={link.href}
              href={`/${lang}/${link.href}`}
              onClick={() => setMobileOpen(false)}
              className="text-sm text-[#cbd5e1] hover:text-white py-2 transition-colors"
            >
              {t(lang, nav[link.key])}
            </Link>
          ))}
          <div className="flex flex-col gap-2 mt-2">
            <Link
              href={`/${lang}/contact`}
              onClick={() => setMobileOpen(false)}
              className="text-sm font-medium text-center px-5 py-2.5 rounded-lg border border-white/[0.3] bg-white/[0.08] text-white hover:bg-white/[0.15] hover:border-white/[0.4] transition-all duration-200"
            >
              {t(lang, nav.contactUs)}
            </Link>
            <a
              href={consoleLoginUrl}
              onClick={() => setMobileOpen(false)}
              className="text-sm font-medium text-center px-5 py-2.5 rounded-lg bg-gradient-to-r from-[var(--brand-600)] to-[var(--brand-500)] transition-all duration-200 btn-lightning"
            >
              {t(lang, nav.loginRegister)}
            </a>
          </div>
        </div>
      </div>
    </header>
  )
}
